import { useState, useEffect, useRef } from 'react';
import { userRequest } from '../utils/requestMethods';
import { Header, TransactionTable } from '../components';
import { useStateContext } from '../contexts/ContextProvider';
import useKYCStatusRedirect from '../hooks/useKYCStatusRedirect';
import { FiCalendar, FiX } from 'react-icons/fi';

const AllTransactions = () => {
  useKYCStatusRedirect("/all-payments");
  const { user, school } = useStateContext();
  const [transactions, setTransactions] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [loading, setLoading] = useState(false);
  const filterRef = useRef(null);

  const [transactionType, setTransactionType] = useState(() => {
    return localStorage.getItem('activeTransactionType') || "all";
  });

  const userId = user?._id;
  const schoolId = school?._id;

  // Close date filter on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) {
        setShowDateFilter(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchTransactions = async (type = transactionType) => {
    if (!userId || !schoolId) return;
    try {
      setLoading(true);
      let url = `transactions/${userId}/${schoolId}?limit=1000&sort_by=date&order=des`;
      if (type !== "all") url += `&type=${type}`;
      const { data } = await userRequest.get(url);
      setTransactions(data.transactions);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, [userId, schoolId]);

  useEffect(() => {
    fetchTransactions();
    localStorage.setItem('activeTransactionType', transactionType);
  }, [transactionType]);

  const submitHandler = async (e) => {
    e.preventDefault();
    if (!startDate || !endDate || !userId || !schoolId) return;
    setLoading(true);
    const start = new Date(startDate).toISOString().slice(0, 10);
    const end = new Date(endDate).toISOString().slice(0, 10);
    try {
      let url = `transactions/${userId}/${schoolId}?limit=200&sort_by=date&order=des&from=${start}&to=${end}`;
      if (transactionType !== "all") url += `&type=${transactionType}`;
      const { data } = await userRequest.get(url);
      setTransactions(data.transactions);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
      setShowDateFilter(false);
    }
  };

  const clearDateFilter = () => {
    setStartDate('');
    setEndDate('');
    setShowDateFilter(false);
    fetchTransactions();
  };

  const hasDateFilter = startDate && endDate;

  return (
    <div className="m-2 md:m-5 mt-24 p-6 md:p-10 dark:bg-secondary-dark-bg bg-white rounded-3xl min-h-screen md:overflow-auto">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
        <Header category="Payments" title="All Payments" />

        {/* Date range filter */}
        <div className="relative shrink-0" ref={filterRef}>
          <button
            type="button"
            onClick={() => setShowDateFilter(!showDateFilter)}
            className={`inline-flex items-center gap-2 py-2.5 px-5 text-sm font-medium rounded-full transition-all
              ${hasDateFilter
                ? 'bg-indigo-50 text-indigo-700 border border-indigo-200'
                : 'bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100'
              }`}
          >
            <FiCalendar className="w-4 h-4" />
            {hasDateFilter ? (
              <span>
                {new Date(startDate).toLocaleDateString('en-NG', { month: 'short', day: 'numeric' })}
                {' — '}
                {new Date(endDate).toLocaleDateString('en-NG', { month: 'short', day: 'numeric' })}
              </span>
            ) : (
              'Date Range'
            )}
            {hasDateFilter && (
              <span
                role="button"
                onClick={(e) => { e.stopPropagation(); clearDateFilter(); }}
                className="ml-1 p-0.5 rounded-full hover:bg-indigo-100"
              >
                <FiX className="w-3.5 h-3.5" />
              </span>
            )}
          </button>

          {showDateFilter && (
            <div className="absolute right-0 top-full mt-2 z-50 bg-white border border-gray-200 shadow-lg rounded-2xl p-5 w-72">
              <p className="text-sm font-semibold text-gray-700 mb-4">Filter by date range</p>
              <div className="space-y-3">
                <div>
                  <label className="block mb-1.5 text-xs font-medium text-gray-500">Start Date</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-xl focus:ring-indigo-500 focus:border-indigo-500 block w-full p-2.5"
                  />
                </div>
                <div>
                  <label className="block mb-1.5 text-xs font-medium text-gray-500">End Date</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-xl focus:ring-indigo-500 focus:border-indigo-500 block w-full p-2.5"
                  />
                </div>
                <div className="flex gap-2 pt-1">
                  <button
                    type="button"
                    onClick={clearDateFilter}
                    className="flex-1 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors"
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    disabled={loading || !startDate || !endDate}
                    onClick={submitHandler}
                    className="flex-1 py-2 text-sm font-medium text-white bg-indigo-600 rounded-full hover:bg-indigo-700 disabled:bg-indigo-300 transition-colors"
                  >
                    {loading ? 'Searching...' : 'Apply'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <TransactionTable
        transactions={transactions}
        loading={loading}
        onTransactionTypeChange={setTransactionType}
        activeTransactionType={transactionType}
      />
    </div>
  );
};

export default AllTransactions;
